(function () {
  'use strict';

  var BLACK = { r: 0, g: 0, b: 0 };
  var WHITE = { r: 255, g: 255, b: 255 };
  var MIN_TEXT_CONTRAST = 4.5;
  var MIN_SURFACE_CONTRAST = 1.35;
  var HIGH_PAIR_CONTRAST = 9;
  var TONAL_MIX_START = 0.12;
  var TONAL_MIX_MAX = 0.64;
  var TONAL_MIX_STEP = 0.02;

  function clamp(value) {
    return Math.max(0, Math.min(255, value));
  }

  function parseCssColor(value) {
    if (!value) return null;

    var match = value.match(/rgba?\(\s*([\d.]+)[,\s]+([\d.]+)[,\s]+([\d.]+)(?:\s*[,\/]\s*([\d.]+))?/i);
    if (!match) return null;

    return {
      r: clamp(parseFloat(match[1])),
      g: clamp(parseFloat(match[2])),
      b: clamp(parseFloat(match[3])),
      a: match[4] === undefined ? 1 : Math.max(0, Math.min(1, parseFloat(match[4])))
    };
  }

  function parseRgbTriplet(value) {
    if (!value) return null;

    var parts = value.trim().replace(/,/g, ' ').split(/\s+/).filter(Boolean);
    if (parts.length < 3) return null;

    var r = parseFloat(parts[0]);
    var g = parseFloat(parts[1]);
    var b = parseFloat(parts[2]);

    if ([r, g, b].some(function (channel) { return Number.isNaN(channel); })) return null;

    return { r: clamp(r), g: clamp(g), b: clamp(b), a: 1 };
  }

  function isVisibleColor(color) {
    return color && color.a > 0.01;
  }

  function getRenderedBackground(root) {
    var node = root;

    while (node && node.nodeType === 1) {
      var style = window.getComputedStyle(node);
      var rendered = parseCssColor(style.backgroundColor);

      if (isVisibleColor(rendered)) return rendered;

      if (node === root) {
        var themeBackground = parseRgbTriplet(style.getPropertyValue('--color-background'));
        if (isVisibleColor(themeBackground)) return themeBackground;
      }

      node = node.parentElement;
    }

    return { r: 255, g: 255, b: 255, a: 1 };
  }

  function getRenderedTextColor(link) {
    var rendered = parseCssColor(window.getComputedStyle(link).color);
    return isVisibleColor(rendered) ? rendered : { r: 23, g: 23, b: 23, a: 1 };
  }

  function mix(first, second, amount) {
    return {
      r: Math.round(first.r + ((second.r - first.r) * amount)),
      g: Math.round(first.g + ((second.g - first.g) * amount)),
      b: Math.round(first.b + ((second.b - first.b) * amount)),
      a: 1
    };
  }

  function linearize(channel) {
    var value = channel / 255;
    return value <= 0.04045 ? value / 12.92 : Math.pow((value + 0.055) / 1.055, 2.4);
  }

  function luminance(color) {
    return (0.2126 * linearize(color.r)) + (0.7152 * linearize(color.g)) + (0.0722 * linearize(color.b));
  }

  function contrastRatio(first, second) {
    var firstLuminance = luminance(first);
    var secondLuminance = luminance(second);
    var lighter = Math.max(firstLuminance, secondLuminance);
    var darker = Math.min(firstLuminance, secondLuminance);

    return (lighter + 0.05) / (darker + 0.05);
  }

  function toRgb(color) {
    return 'rgb(' + color.r + ', ' + color.g + ', ' + color.b + ')';
  }

  function rgbToHsl(color) {
    var r = color.r / 255;
    var g = color.g / 255;
    var b = color.b / 255;
    var max = Math.max(r, g, b);
    var min = Math.min(r, g, b);
    var delta = max - min;
    var h = 0;
    var s = 0;
    var l = (max + min) / 2;

    if (delta !== 0) {
      s = l > 0.5 ? delta / (2 - max - min) : delta / (max + min);

      if (max === r) h = ((g - b) / delta) + (g < b ? 6 : 0);
      else if (max === g) h = ((b - r) / delta) + 2;
      else h = ((r - g) / delta) + 4;

      h *= 60;
    }

    return { h: h, s: s, l: l };
  }

  function hueToRgb(p, q, t) {
    if (t < 0) t += 1;
    if (t > 1) t -= 1;
    if (t < 1 / 6) return p + ((q - p) * 6 * t);
    if (t < 1 / 2) return q;
    if (t < 2 / 3) return p + ((q - p) * (2 / 3 - t) * 6);
    return p;
  }

  function hslToRgb(h, s, l) {
    h = ((h % 360) + 360) % 360;
    var r;
    var g;
    var b;

    if (s === 0) {
      r = g = b = l;
    } else {
      var q = l < 0.5 ? l * (1 + s) : l + s - (l * s);
      var p = (2 * l) - q;
      var hue = h / 360;
      r = hueToRgb(p, q, hue + (1 / 3));
      g = hueToRgb(p, q, hue);
      b = hueToRgb(p, q, hue - (1 / 3));
    }

    return {
      r: Math.round(r * 255),
      g: Math.round(g * 255),
      b: Math.round(b * 255),
      a: 1
    };
  }

  function chooseTonalTarget(background, text) {
    var backgroundLum = luminance(background);
    var textLum = luminance(text);

    if (backgroundLum < 0.25 && textLum < 0.25) return WHITE;
    if (backgroundLum > 0.65 && textLum > 0.65) return BLACK;

    return textLum > backgroundLum ? BLACK : WHITE;
  }

  function createTonalHoverSurface(background, text) {
    var target = chooseTonalTarget(background, text);
    var best = background;
    var bestScore = -Infinity;
    var amount = TONAL_MIX_START;

    while (amount <= TONAL_MIX_MAX + 0.001) {
      var candidate = mix(background, target, amount);
      var textContrast = contrastRatio(text, candidate);
      var surfaceContrast = contrastRatio(background, candidate);
      var readable = textContrast >= MIN_TEXT_CONTRAST;
      var score = (readable ? 100 : 0) + (surfaceContrast * 12) + textContrast;

      if (score > bestScore) {
        best = candidate;
        bestScore = score;
      }

      if (readable && surfaceContrast >= MIN_SURFACE_CONTRAST) {
        return candidate;
      }

      amount += TONAL_MIX_STEP;
    }

    return best;
  }

  function buildChromaticHues(background, text) {
    var backgroundHsl = rgbToHsl(background);
    var textHsl = rgbToHsl(text);
    var baseHue;

    if (backgroundHsl.s >= 0.12) baseHue = backgroundHsl.h;
    else if (textHsl.s >= 0.12) baseHue = textHsl.h;
    else baseHue = 215;

    return [
      baseHue + 120,
      baseHue + 180,
      baseHue + 240,
      215,
      0,
      285,
      165,
      35
    ];
  }

  function createChromaticHoverSurface(background, text) {
    var hues = buildChromaticHues(background, text);
    var lightnesses = [0.32, 0.36, 0.40, 0.44, 0.48, 0.52, 0.56];
    var saturations = [1, 0.92, 0.84];
    var best = null;
    var bestScore = -Infinity;

    hues.forEach(function (hue) {
      saturations.forEach(function (saturation) {
        lightnesses.forEach(function (lightness) {
          var candidate = hslToRgb(hue, saturation, lightness);
          var textContrast = contrastRatio(text, candidate);
          var surfaceContrast = contrastRatio(background, candidate);

          if (textContrast < MIN_TEXT_CONTRAST) return;

          var score = (surfaceContrast * 18) + (textContrast * 2) + (saturation * 2);

          if (score > bestScore) {
            best = candidate;
            bestScore = score;
          }
        });
      });
    });

    return best;
  }

  function createAdaptiveHoverSurface(background, text) {
    var pairContrast = contrastRatio(background, text);

    if (pairContrast >= HIGH_PAIR_CONTRAST) {
      var chromatic = createChromaticHoverSurface(background, text);
      if (chromatic) return chromatic;
    }

    return createTonalHoverSurface(background, text);
  }

  function applyAdaptiveHover(root) {
    if (!root) return;

    var background = getRenderedBackground(root);

    root.querySelectorAll('.solbeli-header-nav__link, .solbeli-header-nav__mobile-link').forEach(function (link) {
      var normalText = getRenderedTextColor(link);
      var hoverSurface = createAdaptiveHoverSurface(background, normalText);

      link.style.setProperty('--solbeli-nav-hover-surface', toRgb(hoverSurface));

      /* El texto conserva exactamente su color normal durante hover/focus. */
      link.style.setProperty('--solbeli-nav-hover-contrast', toRgb(normalText));
    });
  }

  function refreshAdaptiveHover(root) {
    applyAdaptiveHover(root);

    window.requestAnimationFrame(function () {
      applyAdaptiveHover(root);
    });
  }

  function initNavPanel(root) {
    if (!root || root.dataset.solbeliNavInitialized === 'true') return;

    var button = root.querySelector('[data-solbeli-nav-toggle]');
    var panel = root.querySelector('[data-solbeli-nav-panel]');

    if (!button || !panel) return;
    root.dataset.solbeliNavInitialized = 'true';

    function onDocumentClick(event) {
      if (!root.contains(event.target)) close();
    }

    function onKeydown(event) {
      if (event.key === 'Escape' || event.key === 'Esc') {
        close();
        button.focus();
      }
    }

    function open() {
      panel.hidden = false;
      button.setAttribute('aria-expanded', 'true');
      document.addEventListener('click', onDocumentClick);
      document.addEventListener('keydown', onKeydown);
    }

    function close() {
      panel.hidden = true;
      button.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', onDocumentClick);
      document.removeEventListener('keydown', onKeydown);
    }

    button.addEventListener('click', function (event) {
      event.stopPropagation();
      if (button.getAttribute('aria-expanded') === 'true') close();
      else open();
    });

    panel.addEventListener('click', function (event) {
      if (event.target.closest('a')) close();
    });
  }

  function initTouchHover(root) {
    if (!root || root.dataset.solbeliTouchHoverInitialized === 'true') return;
    root.dataset.solbeliTouchHoverInitialized = 'true';

    var links = root.querySelectorAll('.solbeli-header-nav__link, .solbeli-header-nav__mobile-link');
    var activeLink = null;
    var activePointerId = null;
    var startX = 0;
    var startY = 0;
    var startedAt = 0;
    var feedbackTimer = null;
    var longPressTimer = null;
    var minimumFeedbackMs = 220;
    var tapMaxMs = 350;
    var scrollThresholdPx = 8;
    var longPressCancelled = false;

    function clearFeedback() {
      window.clearTimeout(feedbackTimer);
      window.clearTimeout(longPressTimer);
      feedbackTimer = null;
      longPressTimer = null;
      if (activeLink) activeLink.classList.remove('is-touch-hover-active');
      activeLink = null;
      activePointerId = null;
    }

    links.forEach(function (link) {
      link.addEventListener('pointerdown', function (event) {
        if (event.pointerType !== 'touch' && event.pointerType !== 'pen') return;
        if (event.isPrimary === false) return;
        clearFeedback();
        activeLink = link;
        activePointerId = event.pointerId;
        startX = event.clientX;
        startY = event.clientY;
        startedAt = window.performance.now();
        longPressCancelled = false;
        longPressTimer = window.setTimeout(function () {
          longPressCancelled = true;
        }, tapMaxMs);
      });

      link.addEventListener('pointermove', function (event) {
        if (activeLink !== link || activePointerId !== event.pointerId) return;
        if (Math.abs(event.clientX - startX) > scrollThresholdPx ||
            Math.abs(event.clientY - startY) > scrollThresholdPx) {
          clearFeedback();
        }
      });

      link.addEventListener('pointercancel', function (event) {
        if (activeLink === link && activePointerId === event.pointerId) clearFeedback();
      });

      link.addEventListener('pointerup', function (event) {
        if (activeLink !== link || activePointerId !== event.pointerId) return;
        window.clearTimeout(longPressTimer);
        longPressTimer = null;
        var elapsed = window.performance.now() - startedAt;
        var moved = Math.abs(event.clientX - startX) > scrollThresholdPx ||
                    Math.abs(event.clientY - startY) > scrollThresholdPx;
        activePointerId = null;
        if (longPressCancelled || moved || elapsed >= tapMaxMs) {
          clearFeedback();
          return;
        }
        link.classList.add('is-touch-hover-active');
        feedbackTimer = window.setTimeout(clearFeedback, minimumFeedbackMs);
      });
    });
  }

  function initRoot(root) {
    refreshAdaptiveHover(root);
    initTouchHover(root);
    initNavPanel(root);
  }

  function init() {
    document.querySelectorAll('[data-solbeli-nav-root]').forEach(initRoot);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  window.addEventListener('resize', function () {
    window.requestAnimationFrame(function () {
      document.querySelectorAll('[data-solbeli-nav-root]').forEach(applyAdaptiveHover);
    });
  });

  document.addEventListener('shopify:section:load', function (event) {
    if (!event || !event.target) return;
    event.target.querySelectorAll('[data-solbeli-nav-root]').forEach(initRoot);
  });

  document.addEventListener('shopify:section:select', function (event) {
    if (!event || !event.target) return;
    event.target.querySelectorAll('[data-solbeli-nav-root]').forEach(refreshAdaptiveHover);
  });
})();
